<template>
  <EcxToolbar
    :show-toggle="true"
    :sidebar-expanded="expanded"
    :show-profile="true"
    :show-profile-menu-items="true"
    :user-name="userName"
    :user-email="userEmail"
    :avatar-url="userAvatarUrl"
    :profile-menu-label="t('corp.toolbar.profile_menu')"
    @toggle="toggleSidebar"
    @logout="handleLogout"
    @settings="handleSettings"
  >
    <!-- Toggle icon customizado -->
    <template #toggle-icon>
      <DrawerToggleBlueIcon v-if="expanded" class="w-5 h-5" />
      <DrawerToggleGrayIcon v-else class="w-5 h-5 text-subtitle-gray" />
    </template>

    <!-- Seletor de empresa (troca sem relogar), ao lado do toggle -->
    <template #left>
      <CompanySwitcher />
    </template>

    <!-- Ações à direita -->
    <template #right>
      <button
        v-if="canImport"
        type="button"
        data-testid="bulk-import-topbar-btn"
        class="relative h-10 w-10 flex items-center justify-center text-subtitle-gray hover:bg-grey-50 rounded-lg transition-colors"
        :title="t('corp.bulk_import.topbar.import_tooltip')"
        :aria-label="t('corp.bulk_import.topbar.import_tooltip')"
        @click="goToBulkImportCentral"
      >
        <TopbarImportIcon class="w-5 h-5" />
        <span
          v-if="bulkActiveCount > 0"
          data-testid="bulk-import-badge"
          aria-hidden="true"
          class="absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-red-500"
        />
        <span
          v-if="bulkActiveCount > 0"
          class="sr-only"
          aria-live="polite"
        >{{ t('corp.bulk_import.topbar.active_count', { count: bulkActiveCount }, bulkActiveCount) }}</span>
      </button>

      <RouterLink
        v-if="canOpenSettings"
        to="/configuracoes"
        class="h-9 w-9 flex items-center justify-center rounded-lg transition-colors"
        :class="isSettingsActive ? 'bg-royal-25 text-royal-500' : 'text-subtitle-gray hover:bg-grey-50'"
        :aria-label="t('corp.toolbar.settings')"
        :aria-current="isSettingsActive ? 'page' : undefined"
      >
        <SettingsOutlineIcon class="w-5 h-5" aria-hidden="true" />
      </RouterLink>
    </template>

  </EcxToolbar>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { RouterLink, useRoute, useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'

import { useSidebarStore } from '@/stores/sidebar'
import { useUserStore } from '@/stores/user'
import { useAuthStore } from '@/stores/auth'
import { useActiveBulkImportsStore } from '@/stores/activeBulkImports'
import { useManagerCan } from '@/composables/users/useManagerCan'

import { EcxToolbar } from '@ecx/ui'
import {
  DrawerToggleBlueIcon,
  DrawerToggleGrayIcon,
  SettingsOutlineIcon,
  TopbarImportIcon,
} from '@ecx/ui/icons'

import CompanySwitcher from './CompanySwitcher.vue'

const router = useRouter()
const route = useRoute()
const { t } = useI18n()
const { can } = useManagerCan()

const canOpenSettings = computed(
  () => can('settings', 'show') || can('cost_centers', 'index') || can('projects', 'index'),
)

const isSettingsActive = computed(() => route.path.startsWith('/configuracoes'))

const sidebarStore = useSidebarStore()
const userStore = useUserStore()
const authStore = useAuthStore()
const activeBulkImportsStore = useActiveBulkImportsStore()

const { expanded } = storeToRefs(sidebarStore)
const { userName, userEmail, userAvatarUrl } = storeToRefs(userStore)
const { activeCount: bulkActiveCount } = storeToRefs(activeBulkImportsStore)

const canImport = computed(() => can('users', 'import') || can('vehicles', 'import'))

const toggleSidebar = () => {
  sidebarStore.toggle()
}

const handleSettings = () => {
  router.push({ name: 'settings-profile' })
}

const handleLogout = () => {
  authStore.logout()
  userStore.clearUser()
}

const goToBulkImportCentral = () => {
  router.push({ name: 'bulk-import-central' })
}
</script>
